import { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { Navigation, RefreshCw, Filter } from 'lucide-react';
import clsx from 'clsx';
import { mapAPI, usersAPI } from '@shared/apiClient';
import ServiceMap, { getTechColor } from '../../components/ServiceMap';
import { useDispatchWorkspace } from './DispatchContext';
import { buildTechColorMap, technicianName } from './constants';

export default function MapViewPanel() {
  const [segment, setSegment] = useState('all');
  const [routeGeometry, setRouteGeometry] = useState(null);
  const {
    focusedTechId,
    setFocusedTechId,
    setSelectedDetail,
  } = useDispatchWorkspace();

  const filterParams = useMemo(() => ({
    segment,
    technicianId: focusedTechId || undefined,
  }), [segment, focusedTechId]);

  const { data: mapData, isLoading, refetch, isFetching } = useQuery(
    ['mapPoints', filterParams],
    () => mapAPI.getPoints(filterParams).then((res) => res.data),
    { keepPreviousData: true, refetchInterval: 30000 }
  );

  const { data: techniciansData } = useQuery(
    ['technicians', 'map'],
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }).then((res) => res.data)
  );

  const technicians = techniciansData?.users || techniciansData?.data || [];
  const techColorMap = useMemo(() => buildTechColorMap(technicians), [technicians]);

  const points = useMemo(() => {
    const raw = mapData?.points || [];
    if (!focusedTechId) return raw;
    return raw.map((p) => ({
      ...p,
      opacity: p.technicianId === focusedTechId ? 1 : p.technicianId ? 0.25 : 0.5,
    }));
  }, [mapData, focusedTechId]);

  const routePlanMutation = useMutation(
    (stops) => mapAPI.planRoute({ stops }).then((res) => res.data),
    {
      onSuccess: (data) => {
        setRouteGeometry(data.geometry);
        toast.success(
          `Route: ${(data.distanceMeters / 1609.34).toFixed(1)} mi · ~${Math.round(data.durationSeconds / 60)} min`
        );
      },
      onError: () => toast.error('Could not plan route'),
    }
  );

  const handlePlanRoute = () => {
    const techPoints = (mapData?.points || [])
      .filter((p) => p.lat != null && p.lng != null && (!focusedTechId || p.technicianId === focusedTechId))
      .map((p) => ({ lat: p.lat, lng: p.lng, id: p.id, label: p.label }));

    if (techPoints.length < 2) {
      toast.error('Need at least 2 stops to plan a route');
      return;
    }
    routePlanMutation.mutate(techPoints);
  };

  const handlePointClick = (point) => {
    setSelectedDetail({ type: 'mapPoint', point });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-shrink-0 px-3 py-2 border-b border-surface-border bg-surface flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-gray-400" />
        <select
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="input w-auto py-1 text-xs"
        >
          <option value="all">All types</option>
          <option value="customer">Customers</option>
          <option value="cspire">CSpire</option>
          <option value="franklin">Franklin</option>
        </select>

        <div className="flex items-center gap-1 overflow-x-auto flex-1">
          <button
            type="button"
            onClick={() => setFocusedTechId('')}
            className={clsx(
              'px-2 py-0.5 rounded text-2xs font-medium whitespace-nowrap',
              !focusedTechId ? 'bg-gray-900 text-white' : 'bg-surface-secondary text-gray-600 hover:bg-surface-hover'
            )}
          >
            All techs
          </button>
          {technicians.filter((t) => t.isActive).map((tech) => (
            <button
              key={tech.id}
              type="button"
              onClick={() => setFocusedTechId(focusedTechId === tech.id ? '' : tech.id)}
              className={clsx(
                'px-2 py-0.5 rounded text-2xs font-medium whitespace-nowrap border',
                focusedTechId === tech.id ? 'text-white border-transparent' : 'bg-surface text-gray-600 border-surface-border hover:bg-surface-hover'
              )}
              style={focusedTechId === tech.id ? { backgroundColor: techColorMap[tech.id] || getTechColor(tech.id) } : undefined}
            >
              {tech.firstName?.[0]}{tech.lastName?.[0]}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handlePlanRoute}
          disabled={routePlanMutation.isLoading}
          className="btn-sm btn-secondary"
          title="Plan route for focused tech"
        >
          <Navigation className="h-3.5 w-3.5 mr-1" />
          Route
        </button>
        <button
          type="button"
          onClick={() => refetch()}
          className="btn-sm btn-ghost"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {focusedTechId && (
        <div className="flex-shrink-0 px-3 py-1 bg-primary-50 border-b border-primary-100 text-xs text-primary-800">
          Tech focus: {technicianName(technicians.find((t) => t.id === focusedTechId))}
          {' · '}
          {(mapData?.points || []).filter((p) => p.technicianId === focusedTechId).length} stops
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">Loading map...</div>
        ) : (
          <ServiceMap
            points={points}
            colorMode={focusedTechId ? 'technician' : 'segment'}
            techColorMap={techColorMap}
            routeGeometry={routeGeometry}
            onPointClick={handlePointClick}
            height="100%"
            className="h-full"
          />
        )}
      </div>
    </div>
  );
}
